import {
  button,
  span
} from '@srfnstack/fntags'
import { creditActions } from '../state.js'

/**
 * A button that displays the credit cost of an action.
 * @param actionCode The code of the credit action to display the cost for.
 * @param label The label of the button. Can be a string or a Node.
 * @param multipliers An object or fnstate containing multiplier values for the action (e.g. { batch_size: 20 }).
 * @param className CSS classes for the button.
 * @param onclick Click handler.
 * @param disabled Disabled state (can be fnstate).
 */
export const CreditButton = ({ actionCode, label, multipliers = {}, class: className, onclick, disabled, ...rest }) => {
  const renderCost = (actions, mults) => {
    const action = actions.find(a => a.code === actionCode)
    if (!action) return ''

    let cost = 0
    if (action.cost_config) {
      cost = action.cost_config.static || 0
      if (action.cost_config.multipliers) {
        for (const [field, base] of Object.entries(action.cost_config.multipliers)) {
          const val = mults[field]
          const multVal = (val && val.isFnState) ? val() : (val || 1)
          cost += base * multVal
        }
      }
    } else {
      cost = action.cost
    }

    if (cost <= 0) return ''

    return span({ class: 'text-[10px] opacity-60 ml-2 font-bold inline-flex items-center gap-0.5' },
      '(',
      cost.toString(),
      span({ class: 'material-symbols-rounded !text-[12px]' }, 'token'),
      ')'
    )
  }

  return creditActions.bindAs(actions => {
    const btnAttrs = {
      class: className,
      onclick,
      disabled,
      ...rest
    }

    if (multipliers.isFnState) {
      return multipliers.bindAs(mults => button(btnAttrs, label, renderCost(actions, mults)))
    } else {
      // Check if any value in multipliers is a fnstate
      const states = Object.values(multipliers).filter(v => v && v.isFnState)
      if (states.length > 0) {
        // If any value is a state, we need to bind to all of them.
        // For simplicity and to avoid complex multi-binding, we can wrap the whole button in a bindAs
        // that triggers when ANY of the states change.
        // We can use a trick: create a combined state or just subscribe to all.
        // fntags doesn't have a built-in 'combineStates', but we can just use bindAs on the first state found
        // or just use a helper.
        // Given the common use case is just one multiplier state, let's just bind to the first one found for now.
        const firstState = states[0]
        return firstState.bindAs(() => button(btnAttrs, label, renderCost(actions, multipliers)))
      }
      return button(btnAttrs, label, renderCost(actions, multipliers))
    }
  })
}
